import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useBluelagoon } from "@bluelagoon";
import { Button } from "@shadcn-in-nx/ui/button";
import { Textarea } from "@shadcn-in-nx/ui/textarea";
import { Badge } from "@shadcn-in-nx/ui/badge";
import { Loader2, Save } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";

export default function PromptManager() {
    const { t } = useTranslation();
    const [prompt, setPrompt] = useState("");
    const [version, setVersion] = useState(0);
    const [history, setHistory] = useState([]);
    const { call: fetchPrompt, isLoading } = useBluelagoon({ method: "getSupportPrompt" });
    const { call: savePrompt, isLoading: isSaving } = useBluelagoon({ method: "updateSupportPrompt" });

    const load = useCallback(async () => {
        const res = await fetchPrompt().catch(() => null);
        if (res?.data) {
            setPrompt(res.data.prompt || "");
            setVersion(res.data.version || 0);
            setHistory(res.data.history || []);
        }
    }, [fetchPrompt]);

    useEffect(() => { load(); }, [load]);

    const handleSave = async () => {
        const res = await savePrompt({ newPrompt: prompt, reason: "Manual edit from Prompt Manager" }).catch(() => null);
        if (res?.status === 200) {
            toast.success(t("admin.support.promptSaved", "Prompt saved"));
            load();
        } else {
            toast.error(t("admin.support.promptSaveError", "Failed to save prompt"));
        }
    };

    if (isLoading) return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-lg font-semibold">{t("admin.support.systemPrompt", "AI System Prompt")}</h2>
                    <p className="text-sm text-muted-foreground">{t("admin.support.promptDesc", "Configure the base system prompt for AI support replies")}</p>
                </div>
                <Badge variant="secondary">v{version}</Badge>
            </div>
            <Textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={12} className="font-mono text-sm"
                placeholder={t("admin.support.promptPlaceholder", "Enter system prompt...")} />
            <Button onClick={handleSave} disabled={isSaving || !prompt.trim()}>
                {isSaving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                {t("admin.support.savePrompt", "Save Changes")}
            </Button>
            {history.length > 0 && (
                <div>
                    <h3 className="text-sm font-semibold mb-2">{t("admin.support.promptHistory", "Recent Changes")}</h3>
                    <div className="space-y-2">
                        {history.map((h) => (
                            <div key={h.id} className="text-xs border rounded p-2">
                                <div className="flex items-center justify-between">
                                    <span className="font-medium">v{h.version} — {h.appliedBy ? `${h.appliedBy.firstName} ${h.appliedBy.lastName}` : "System"}</span>
                                    <span className="text-muted-foreground">{format(new Date(h.createdAt), "MMM d, yyyy h:mm a")}</span>
                                </div>
                                {h.reason && <p className="text-muted-foreground mt-1">{h.reason}</p>}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
